import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import StepWizard from "./StepWizard";
import { FieldWrap, inputCls, NameFields, PhoneInput, EmailInput, OptionGrid } from "./fields";
import { getTracking } from "@/lib/tracking";
import { submitLead, newSubmissionId, normalizeSubmitError } from "@/lib/submit";
import { emailError, phoneError, formatPhone } from "@/lib/format";

const INVESTOR_TYPES = ["Accredited", "Non-accredited", "Not sure"];
const CAPITAL = ["Under $25k", "$25k-$50k", "$50k-$100k", "$100k-$250k", "$250k+"];
const INTERESTS = ["Private lending", "Equity / JV", "Passive investment", "Guarantor / credit partner", "Deal review", "Not sure"];
const TIMELINES = ["Now", "30-90 days", "3-6 months", "Just learning"];
const CONSENT = "I understand this form is not an offer of securities. Any opportunity will be presented through proper documentation. Brightside Capital Ventures may contact me about my interest.";

const BLANK = { first_name: "", last_name: "", phone: "", email: "", investor_type: "", capital_range: "", interest_type: "", preferred_markets: "", desired_return: "", timeline: "", notes: "", consent: false };

export default function InvestorForm() {
  const navigate = useNavigate();
  const [data, setData] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const submissionId = useRef(newSubmissionId());
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const steps = [
    { id: "contact", label: "About you", title: "How can we reach you?",
      validate: (d) => ({ ...(d.first_name.trim() ? {} : { first_name: "Required" }), ...(d.last_name.trim() ? {} : { last_name: "Required" }), ...(phoneError(d.phone) ? { phone: phoneError(d.phone) } : {}), ...(emailError(d.email) ? { email: emailError(d.email) } : {}) }),
      render: ({ data: d, errors }) => (
        <div className="space-y-5">
          <NameFields first={d.first_name} last={d.last_name} onChange={set} errors={errors} testIdPrefix="investor" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldWrap label="Phone" error={errors.phone}><PhoneInput value={d.phone} onChange={(v) => set("phone", v)} testId="investor-phone" /></FieldWrap>
            <FieldWrap label="Email" error={errors.email}><EmailInput value={d.email} onChange={(v) => set("email", v)} testId="investor-email" /></FieldWrap>
          </div>
        </div>
      ) },
    { id: "profile", label: "Profile", title: "Investor profile",
      render: ({ data: d }) => (
        <div className="space-y-6">
          <FieldWrap label="Investor type"><OptionGrid options={INVESTOR_TYPES} value={d.investor_type} onChange={(v) => set("investor_type", v)} testIdPrefix="investor-type" columns={3} /></FieldWrap>
          <FieldWrap label="Capital range"><OptionGrid options={CAPITAL} value={d.capital_range} onChange={(v) => set("capital_range", v)} testIdPrefix="investor-capital" columns={3} /></FieldWrap>
        </div>
      ) },
    { id: "interest", label: "Interest", title: "What are you most interested in?",
      render: ({ data: d }) => (
        <div className="space-y-6">
          <FieldWrap label="Primary interest"><OptionGrid options={INTERESTS} value={d.interest_type} onChange={(v) => set("interest_type", v)} testIdPrefix="investor-interest" columns={3} /></FieldWrap>
          <FieldWrap label="Timeline"><OptionGrid options={TIMELINES} value={d.timeline} onChange={(v) => set("timeline", v)} testIdPrefix="investor-timeline" columns={4} /></FieldWrap>
        </div>
      ) },
    { id: "preferences", label: "Preferences", title: "Markets, returns and notes", helper: "All optional.",
      render: ({ data: d }) => (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldWrap label="Preferred markets" optional><input className={inputCls} placeholder="Sacramento, Roseville" value={d.preferred_markets} onChange={(e) => set("preferred_markets", e.target.value)} data-testid="investor-markets" /></FieldWrap>
            <FieldWrap label="Desired return profile" optional><input className={inputCls} placeholder="Target IRR / yield" value={d.desired_return} onChange={(e) => set("desired_return", e.target.value)} data-testid="investor-return" /></FieldWrap>
          </div>
          <FieldWrap label="Notes" optional><textarea rows={4} className={inputCls} placeholder="Anything we should know about your goals?" value={d.notes} onChange={(e) => set("notes", e.target.value)} data-testid="investor-notes" /></FieldWrap>
        </div>
      ) },
  ];

  const review = (d) => [
    { stepId: "contact", label: "Name", value: `${d.first_name} ${d.last_name}`.trim() },
    { stepId: "contact", label: "Phone", value: formatPhone(d.phone) },
    { stepId: "contact", label: "Email", value: d.email },
    { stepId: "profile", label: "Investor type", value: d.investor_type },
    { stepId: "profile", label: "Capital range", value: d.capital_range },
    { stepId: "interest", label: "Primary interest", value: d.interest_type },
    { stepId: "interest", label: "Timeline", value: d.timeline },
    { stepId: "preferences", label: "Preferred markets", value: d.preferred_markets },
    { stepId: "preferences", label: "Desired return profile", value: d.desired_return },
    { stepId: "preferences", label: "Notes", value: d.notes },
  ];

  const submit = async () => {
    setSubmitting(true); setSubmitError(null);
    const tracking = getTracking();
    try {
      const lead = await submitLead("/api/leads/investor", { ...data, ...tracking, source: tracking.utm_source || "direct" }, submissionId.current);
      submissionId.current = newSubmissionId();
      navigate("/thank-you", { state: { kind: "investor", lead } });
    } catch (err) {
      const norm = normalizeSubmitError(err);
      setSubmitError(norm);
      toast.error(norm.kind === "validation" ? "Please check the highlighted answers" : "Submission not saved yet");
    } finally { setSubmitting(false); }
  };

  return <StepWizard steps={steps} data={data} setField={set} review={review} consentLabel={CONSENT} onSubmit={submit} submitting={submitting} submitError={submitError} submitLabel="Submit Interest" testIdPrefix="investor" />;
}
